'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';

interface FooterCategory {
  id: number;
  name: string;
  slug: string;
}

export default function Footer() {
  // Same /api/categories the header/mobile nav already use - real category
  // names as anchor text instead of the old dead href="#" placeholder links,
  // and every one of these gets sitewide link equity from being in the footer.
  const [categories, setCategories] = useState<FooterCategory[]>([]);

  useEffect(() => {
    fetch('/api/categories')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setCategories(data?.data || []))
      .catch(() => undefined);
  }, []);

  return (
    <footer className="bg-[#1c1917] text-stone-300 py-7 sm:py-12 md:py-16">
      <div className="w-full px-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 sm:gap-8 mb-6 sm:mb-12">
          {/* Brand */}
          <div>
            <h3 className="text-white font-serif font-bold text-lg mb-4">Maroth Jewels</h3>
            <p className="text-sm text-stone-400 leading-relaxed">
              Maroth Jewels Private Limited is a trusted jewelry manufacturer based in Jaipur, India.
            </p>
            <div className="mt-4 flex gap-3">
              <a href="#" className="text-stone-400 hover:text-[var(--orange-primary)] transition">f</a>
              <a href="#" className="text-stone-400 hover:text-[var(--orange-primary)] transition">🐦</a>
              <a href="#" className="text-stone-400 hover:text-[var(--orange-primary)] transition">📷</a>
            </div>
          </div>

          {/* Popular Categories - real category links, sitewide, so every
              page passes internal link equity to these product pages. */}
          <div>
            <h4 className="text-white font-bold mb-4 text-sm">POPULAR CATEGORIES</h4>
            <ul className="space-y-2 text-xs text-stone-400">
              {categories.slice(0, 6).map((cat) => (
                <li key={cat.id}>
                  <a href={`/category/${cat.slug}`} className="hover:text-[var(--orange-primary)] transition">
                    {cat.name}
                  </a>
                </li>
              ))}
              <li className="pt-1.5 border-t border-stone-700/70">
                <a href="/categories" className="hover:text-[var(--orange-primary)] transition font-semibold">
                  View All Categories →
                </a>
              </li>
            </ul>
          </div>

          {/* Solutions - manufacturer-positioning anchor text (private
              label / custom / wholesale manufacturing - what Maroth
              actually does) stacked onto the two real pages that already
              carry that content, same anchor-diversity mechanism as the
              Popular Categories column above. */}
          <div>
            <h4 className="text-white font-bold mb-4 text-sm">SOLUTIONS</h4>
            <ul className="space-y-2 text-xs text-stone-400">
              <li>
                <a href="/private-label-jewelry-manufacturers" className="hover:text-[var(--orange-primary)] transition">
                  Private Label Jewelry Manufacturer
                </a>
              </li>
              <li>
                <a href="/about" className="hover:text-[var(--orange-primary)] transition">
                  Custom Jewelry Manufacturer
                </a>
              </li>
              <li>
                <a href="/about" className="hover:text-[var(--orange-primary)] transition">
                  Wholesale Jewelry Manufacturer
                </a>
              </li>
              <li>
                <a href="/about" className="hover:text-[var(--orange-primary)] transition">
                  Jewelry Manufacturer in Jaipur
                </a>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-bold mb-4 text-sm">COMPANY</h4>
            <ul className="space-y-2 text-xs text-stone-400">
              <li><a href="/about" className="hover:text-[var(--orange-primary)] transition">About Us</a></li>
              <li><a href="/collections" className="hover:text-[var(--orange-primary)] transition">Our Collections</a></li>
              <li><a href="/custom-jewelry-request" className="hover:text-[var(--orange-primary)] transition">Custom Request</a></li>
              <li><a href="/blog" className="hover:text-[var(--orange-primary)] transition">Blog</a></li>
              <li className="pt-1.5 border-t border-stone-700/70">
                <a
                  href={`${process.env.NEXT_PUBLIC_LARAVEL_APP_URL || 'https://www.marothjewels.com'}/admin`}
                  className="hover:text-[var(--orange-primary)] text-[var(--orange-primary)] font-semibold transition"
                >
                  Admin Portal ➔
                </a>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-white font-bold mb-4 text-sm">SUPPORT</h4>
            <ul className="space-y-2 text-xs text-stone-400">
              <li><a href="/contact" className="hover:text-[var(--orange-primary)] transition">Contact Us</a></li>
              <li><a href="/support-policy" className="hover:text-[var(--orange-primary)] transition">Support Policy</a></li>
              <li><a href="/return-policy" className="hover:text-[var(--orange-primary)] transition">Shipping Info</a></li>
              <li><a href="/return-policy" className="hover:text-[var(--orange-primary)] transition">Returns</a></li>
              <li><a href="/terms" className="hover:text-[var(--orange-primary)] transition">Terms &amp; Conditions</a></li>
              <li><a href="/privacy-policy" className="hover:text-[var(--orange-primary)] transition">Privacy Policy</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold mb-4 text-sm">CONTACT</h4>
            <ul className="space-y-3 text-xs text-stone-400">
              <li>
                <a href="tel:+918003816293" className="hover:text-[var(--orange-primary)] transition flex items-center gap-2">
                  📞 +91 8003816293
                </a>
              </li>
              <li>
                <a href="mailto:sales@marothjewels.com" className="hover:text-[var(--orange-primary)] transition flex items-center gap-2">
                  📧 sales@marothjewels.com
                </a>
              </li>
              <li className="flex items-center gap-2">
                📍 Jaipur, India
              </li>
            </ul>
          </div>
        </div>

        {/* Mobile App Badges - same store listings/badges as the live site,
            shown on both mobile and desktop here (the live site only shows
            this row on desktop). */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 mb-6 sm:mb-10">
          <h4 className="text-white font-bold text-sm shrink-0">GET OUR APP</h4>
          <div className="flex gap-3">
            <a
              href="https://play.google.com/store/apps/details?id=com.marothjewels.androidapp&hl=en_US"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="hover:opacity-80 transition"
            >
              <Image
                src="/play-badge.png"
                alt="Get it on Google Play"
                width={132}
                height={44}
                className="h-10 w-auto rounded"
              />
            </a>
            <a
              href="https://apps.apple.com/us/app/maroth-jewels-private-limited/id1558469918"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="hover:opacity-80 transition"
            >
              <Image
                src="/app-store-badge.png"
                alt="Download on the App Store"
                width={132}
                height={44}
                className="h-10 w-auto rounded"
              />
            </a>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center text-xs text-stone-500">
            <p>&copy; 2025 Maroth Jewels Private Limited. All rights reserved.</p>
            <div className="flex gap-6 mt-4 md:mt-0">
              <a href="/privacy-policy" className="hover:text-gray-300 transition">Privacy Policy</a>
              <a href="/terms" className="hover:text-gray-300 transition">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
